import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Users, Eye, Clock, FileText, Bell, Plus, Trash2, Loader2, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyUpdate = { title_en: "", title_ar: "", summary_en: "", summary_ar: "", category: "General", date: "", source_label: "", source_url: "", show_source: true };

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(email, password); toast.success("Welcome back"); }
    catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };
  const inp = "w-full h-12 px-4 rounded-xl border border-line bg-ivory focus:outline-none focus:border-navy focus:ring-4 focus:ring-navy/10";
  return (
    <div className="min-h-screen grid place-items-center bg-surface px-4">
      <form onSubmit={submit} data-testid="admin-login" className="w-full max-w-sm bg-white rounded-2xl border border-line shadow-lift p-8">
        <div className="font-display text-2xl text-ink">TANSEEQ Admin</div>
        <p className="text-sm text-muted mt-1 mb-6">Super-admin dashboard</p>
        <label className="text-sm font-medium text-ink/80">Email</label>
        <input data-testid="admin-email" className={inp + " mt-1 mb-4"} value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr" />
        <label className="text-sm font-medium text-ink/80">Password</label>
        <input data-testid="admin-password" type="password" className={inp + " mt-1 mb-6"} value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" />
        <button data-testid="admin-login-submit" disabled={loading} className="w-full h-12 rounded-full bg-navy text-white font-semibold hover:bg-navy-600 transition-colors inline-flex items-center justify-center gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
        </button>
      </form>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-line bg-white p-6 shadow-card">
      <div className="flex items-center gap-3 text-muted"><Icon className="h-5 w-5 text-navy" /><span className="text-sm">{label}</span></div>
      <div className="font-display text-4xl text-ink mt-3" dir="ltr">{value}</div>
    </div>
  );
}

function Dashboard() {
  const { user, logout, authHeader } = useAuth();
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState(emptyUpdate);
  const [editId, setEditId] = useState(null);
  const H = { headers: authHeader };

  const load = () => {
    axios.get(`${API}/analytics/summary`, H).then((r) => setSummary(r.data)).catch(() => {});
    axios.get(`${API}/updates`).then((r) => setUpdates(r.data)).catch(() => {});
    axios.get(`${API}/contact`, H).then((r) => setLeads(r.data)).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line

  const saveUpdate = async (e) => {
    e.preventDefault();
    try {
      if (editId) await axios.put(`${API}/updates/${editId}`, form, H);
      else await axios.post(`${API}/updates`, form, H);
      toast.success("Saved"); setForm(emptyUpdate); setEditId(null); load();
    } catch (err) { toast.error("Save failed"); }
  };
  const del = async (id) => { await axios.delete(`${API}/updates/${id}`, H); toast.success("Deleted"); load(); };
  const editRow = (u) => { setForm({ ...emptyUpdate, ...u }); setEditId(u.id); setTab("updates"); window.scrollTo(0, 0); };

  const maxDay = Math.max(1, ...(summary?.by_day || []).map((d) => d.views));
  const inp = "w-full h-11 px-3 rounded-lg border border-line focus:outline-none focus:border-navy focus:ring-2 focus:ring-navy/10";

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-navy-deep text-white">
        <div className="container-tnsq flex items-center justify-between h-16">
          <div className="font-display text-xl">TANSEEQ <span className="text-gold">Admin</span></div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-white/60 hidden sm:inline" dir="ltr">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-white/80 hover:text-white"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="container-tnsq py-8">
        <div className="flex gap-2 mb-8">
          {[["overview", "Overview"], ["updates", "Tax Updates"], ["leads", "Leads"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-navy text-white" : "bg-white border border-line text-ink/70 hover:text-navy"}`}>{l}</button>
          ))}
        </div>

        {tab === "overview" && summary && (
          <div data-testid="overview-panel">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Stat icon={Eye} label="Page views" value={summary.total_pageviews} />
              <Stat icon={Users} label="Unique visitors" value={summary.unique_visitors} />
              <Stat icon={Clock} label="Avg. time (s)" value={summary.avg_seconds} />
              <Stat icon={Bell} label="Leads" value={summary.leads} />
              <Stat icon={FileText} label="Updates" value={summary.updates} />
            </div>
            <div className="grid lg:grid-cols-2 gap-6 mt-6">
              <div className="rounded-2xl border border-line bg-white p-6 shadow-card">
                <div className="flex items-center gap-2 font-semibold text-ink mb-4"><BarChart3 className="h-5 w-5 text-navy" /> Views (last 14 days)</div>
                <div className="flex items-end gap-1.5 h-40">
                  {(summary.by_day || []).length === 0 && <p className="text-sm text-muted">No data yet.</p>}
                  {(summary.by_day || []).map((d) => (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-navy/80 rounded-t" style={{ height: `${(d.views / maxDay) * 100}%` }} title={`${d.day}: ${d.views}`} />
                      <span className="text-[0.6rem] text-muted">{d.day.slice(5)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-line bg-white p-6 shadow-card">
                <div className="font-semibold text-ink mb-4">Top pages</div>
                <ul className="space-y-2">
                  {(summary.top_pages || []).length === 0 && <p className="text-sm text-muted">No data yet.</p>}
                  {(summary.top_pages || []).map((p) => (
                    <li key={p.path} className="flex justify-between text-sm border-b border-line pb-2">
                      <span className="text-ink/80" dir="ltr">{p.path}</span><span className="font-semibold text-navy">{p.views}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {tab === "updates" && (
          <div className="grid lg:grid-cols-[1fr_1fr] gap-6" data-testid="updates-panel">
            <form onSubmit={saveUpdate} className="rounded-2xl border border-line bg-white p-6 shadow-card space-y-3 h-fit">
              <div className="font-semibold text-ink flex items-center gap-2"><Plus className="h-4 w-4" /> {editId ? "Edit update" : "Add update"}</div>
              <input className={inp} placeholder="Title (English)" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} required />
              <input className={inp} placeholder="العنوان (عربي)" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} dir="rtl" />
              <textarea className={inp + " h-auto py-2"} rows={2} placeholder="Summary (English)" value={form.summary_en} onChange={(e) => setForm({ ...form, summary_en: e.target.value })} />
              <textarea className={inp + " h-auto py-2"} rows={2} placeholder="الملخص (عربي)" value={form.summary_ar} onChange={(e) => setForm({ ...form, summary_ar: e.target.value })} dir="rtl" />
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                <input className={inp} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <input className={inp} placeholder="Source label (official only)" value={form.source_label} onChange={(e) => setForm({ ...form, source_label: e.target.value })} />
              <input className={inp} placeholder="Source URL" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} dir="ltr" />
              <label className="flex items-center gap-2 text-sm text-ink/70"><input type="checkbox" checked={form.show_source} onChange={(e) => setForm({ ...form, show_source: e.target.checked })} /> Show source (uncheck for blog/unverified)</label>
              <div className="flex gap-2">
                <button data-testid="save-update" className="h-11 px-6 rounded-full bg-navy text-white font-semibold hover:bg-navy-600">{editId ? "Update" : "Publish"}</button>
                {editId && <button type="button" onClick={() => { setForm(emptyUpdate); setEditId(null); }} className="h-11 px-6 rounded-full border border-line">Cancel</button>}
              </div>
            </form>
            <div className="space-y-3">
              {updates.map((u) => (
                <div key={u.id} className="rounded-xl border border-line bg-white p-4 shadow-card">
                  <div className="flex justify-between gap-3">
                    <div><div className="text-xs text-brandgreen font-semibold">{u.category} · {u.date}</div><div className="font-semibold text-ink mt-1">{u.title_en}</div></div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => editRow(u)} className="text-navy text-sm font-semibold">Edit</button>
                      <button onClick={() => del(u.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="rounded-2xl border border-line bg-white shadow-card overflow-x-auto" data-testid="leads-panel">
            <table className="w-full text-sm">
              <thead><tr className="text-start text-muted border-b border-line">
                {["Name", "Phone", "Email", "Service", "Message", "Date"].map((h) => <th key={h} className="text-start font-semibold px-4 py-3">{h}</th>)}
              </tr></thead>
              <tbody>
                {leads.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-muted">No leads yet.</td></tr>}
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-line/70">
                    <td className="px-4 py-3 font-medium text-ink">{l.name}</td>
                    <td className="px-4 py-3" dir="ltr">{l.phone}</td>
                    <td className="px-4 py-3" dir="ltr">{l.email}</td>
                    <td className="px-4 py-3">{l.service}</td>
                    <td className="px-4 py-3 max-w-xs truncate">{l.message}</td>
                    <td className="px-4 py-3 text-muted" dir="ltr">{(l.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { token, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center bg-surface"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;
  return token ? <Dashboard /> : <Login />;
}
